import { useState, useRef } from 'react';
import NoPhiNotice from './NoPhiNotice';
import { httpsCallable } from '../lib/callable';
import { Upload, FileText, Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { functions } from '../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../constants/firebase';
import { createProjectErrorMessage } from '../features/projects/projectLimitMessage';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { functionsBase } from '../config/appConfig';

interface UploadCrfPacketModalProps {
  /** When provided, forms are added to this project. When absent, a new project is created from the packet. */
  existingProjectId?: string;
  /** Short Name already typed in the creation wizard; the field starts here. */
  initialShortName?: string;
  /**
   * Initial state of the "This file is a single CRF" checkbox — a property of
   * the ENTRY POINT the user clicked, never of whether a project exists.
   *
   * Required, and deliberately not defaulted. It used to be derived here as
   * `!!existingProjectId`, which meant the button labelled "Upload CRF Packet"
   * opened with single-form mode pre-ticked and silently merged a 42-form
   * packet into one 840-variable form (2026-08-21, project 8Ko7NuBf…). The
   * caller knows which button was pressed; this component does not.
   */
  defaultSingleForm: boolean;
  /** Called after extraction succeeds with the project id (new or existing) and the form count. */
  onComplete: (projectId: string, formsExtracted: number) => void;
  /** Called if the user cancels before extraction starts. */
  onClose: () => void;
}

type Stage = 'picking' | 'creating' | 'uploading' | 'extracting' | 'done' | 'partial' | 'error';

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx'];
const ALLOWED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

function contentTypeFor(file: File): string {
  if (file.type && ALLOWED_MIME.includes(file.type)) return file.type;
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (lower.endsWith('.doc')) return 'application/msword';
  return 'application/octet-stream';
}

/** Default single-CRF name derived from the uploaded filename, so the extracted
 *  form is findable by the name the user recognizes (rather than a model-
 *  inferred content title). Editable in the modal. */
function defaultFormName(fileName: string): string {
  return (
    fileName
      .replace(/\.(pdf|docx?|doc)$/i, '')
      .replace(/_+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || 'Uploaded CRF'
  );
}

export default function UploadCrfPacketModal({
  existingProjectId,
  defaultSingleForm,
  initialShortName,
  onComplete,
  onClose,
}: UploadCrfPacketModalProps) {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>('picking');
  const [status, setStatus] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [formsExtracted, setFormsExtracted] = useState(0);
  const [extractedTitle, setExtractedTitle] = useState<string>('');
  // `error` is intentionally kept as local state because it backs the
  // persistent stage='error' view's message text — toasts auto-dismiss
  // and would disappear before the user reads the failure reason. Only
  // the transient file-validation errors below the picker use toasts.
  const [error, setError] = useState('');
  // The resolved project id — equal to existingProjectId when uploading
  // into an existing project, or the freshly minted id when this is a
  // new-project flow. Persisted in state (rather than just a closure
  // variable in run()) so the partial-stage "Keep N forms" button can
  // call onComplete with the right project id.
  const [resolvedProjectId, setResolvedProjectId] = useState<string>(existingProjectId || '');
  const [dragActive, setDragActive] = useState(false);
  // Single-form mode. The caller decides the initial state, because only the
  // caller knows which button opened this modal: "Upload CRF" means one form,
  // "Upload CRF Packet" means several. Still user-overridable via the checkbox.
  const [singleForm, setSingleForm] = useState<boolean>(defaultSingleForm);
  // Single-form name — pre-filled from the filename so the extracted form is
  // findable by the name the user recognizes; editable. Only sent (and only
  // honored by the extractor) in single-form mode.
  const [formName, setFormName] = useState<string>('');
  // Study Short Name for the NEW-project flow. Required: it is stamped on
  // every deliverable filename, and nothing may invent one on the user's
  // behalf. Not shown when adding forms to an existing project.
  // Seeded from the creation wizard when it asked first, so the same answer is
  // not demanded twice. Still required, still editable, still enforced by the
  // server — this only decides what the box STARTS as.
  const [shortName, setShortName] = useState<string>(initialShortName ?? '');
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelected = (picked: File) => {
    const lower = picked.name.toLowerCase();
    const validExt = ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
    if (!validExt) {
      showToast('Only PDF, DOC, and DOCX files are supported', 'error');
      return;
    }
    setFile(picked);
    setFormName(defaultFormName(picked.name));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileSelected(f);
  };

  const run = async () => {
    if (!user) {
      showToast('You must be logged in.', 'error');
      return;
    }
    if (!file) return;
    setError('');
    setWarnings([]);
    try {
      // 1. Create the project with a temporary placeholder name when this is
      //    a new-project flow. The extractor will overwrite `name` and
      //    `shortName` with the study title parsed from the packet, so the
      //    placeholder here is only visible during the extraction window.
      let projectId = existingProjectId;
      if (!projectId) {
        setStage('creating');
        setStatus('Creating project...');

        // Org stamping (multi-tenant isolation): the create rules require
        // every new project + owner-member doc to carry the caller's orgId
        // token claim — see the matching path in ProtocolUploader.
        const claims = (await user.getIdTokenResult()).claims;
        const orgId = typeof claims.orgId === 'string' ? claims.orgId : undefined;
        if (!orgId) {
          throw new Error(
            "Your account isn't part of an organization yet. " +
            'Ask your administrator to add you before creating projects.'
          );
        }

        const placeholderName = `Extracting: ${file.name.replace(/\.(pdf|docx?|doc)$/i, '')}`;

        // Create through the callable, never with a direct setDoc. The client
        // cannot enforce a required, sanitized, unique Short Name, nor the
        // per-account project limit — this path used to bypass both, because
        // firestore.rules permits any signed-in user to create a project
        // document. The callable also mints the owner member row.
        //
        // The NAME stays a placeholder because the extractor overwrites it
        // with the real study title moments later. The SHORT NAME does not:
        // it is the user's identifier and nothing derives one.
        const createProjectFn = httpsCallable<
          Record<string, unknown>,
          { projectId: string }
        >(functions, CLOUD_FUNCTIONS.CREATE_PROJECT, { timeout: CALLABLE_TIMEOUTS.SHORT });
        let created;
        try {
          created = await createProjectFn({
            name: placeholderName,
            shortName,
            description: 'Created from uploaded CRF packet',
            studyType: 'interventional',
          });
        } catch (createErr) {
          // Never render the callable's own message. The helper keys on the
          // structured `details.reason`, so a Short Name refusal reads as the
          // field the user can fix rather than as a mystery failure.
          setStage('picking');
          setError(createProjectErrorMessage(createErr));
          return;
        }
        projectId = created.data.projectId;
      }
      // Mirror to state so the partial-stage Keep button has it (the
      // closure-only `projectId` above isn't readable from the JSX).
      setResolvedProjectId(projectId);

      // 2. Signed upload URL — requires Firebase ID token + projectId.
      setStage('uploading');
      setStatus('Securing upload channel...');
      const API_BASE = functionsBase();
      const contentType = contentTypeFor(file);
      const idToken = await user.getIdToken();
      // getUploadUrlCore: lean-codebase build of the same endpoint (see
      // functions-core/), immune to the shared bundle's startup-memory growth.
      const urlRes = await fetch(`${API_BASE}/getUploadUrlCore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          projectId,
          fileName: file.name,
          storagePath: 'crfPackets',
          contentType,
        }),
      });
      if (!urlRes.ok) {
        // Surface the server's actual error instead of masking every non-2xx as
        // the same generic string.
        const serverError = await urlRes.json().then(b => b?.error).catch(() => null);
        throw new Error(serverError || `Failed to get upload URL (HTTP ${urlRes.status})`);
      }
      const { uploadUrl, filePath } = await urlRes.json();

      // 3. Upload file.
      setStatus('Uploading CRF packet...');
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (!putRes.ok) throw new Error('Failed to upload file');

      // 4. Extract. Two-pass Gemini flow + Pass-1.5/1.6 subdivision retries.
      //    `extractCrfPacket` is a Gen-2 Cloud Function with a 30-min server
      //    timeout (`timeoutSeconds: 1800`); the client SDK's default 70s
      //    deadline would race far below that, so we use the EXTRACTOR tier
      //    (31 min, with a 60s client-over-server buffer to avoid the
      //    same `deadline-exceeded` race that XLONG was added to fix).
      //    `updateProjectName` tells the extractor to replace our
      //    placeholder name with the study title it reads out of the
      //    packet — that's why we don't ask the user to type one.
      setStage('extracting');
      setStatus('Reading forms from your packet...');
      const extract = httpsCallable<
        { projectId: string; storagePath: string; updateProjectName: boolean; singleForm: boolean; formName?: string },
        {
          formsExtracted: number;
          recordIds: string[];
          warnings: string[];
          studyTitle?: string;
          chunksFailed?: number;
          chunksTotal?: number;
        }
      >(functions, CLOUD_FUNCTIONS.EXTRACT_CRF_PACKET, { timeout: CALLABLE_TIMEOUTS.EXTRACTOR });
      const result = await extract({
        projectId,
        storagePath: filePath,
        updateProjectName: !existingProjectId,
        singleForm,
        ...(singleForm ? { formName: formName.trim() || undefined } : {}),
      });
      const extracted = result.data?.formsExtracted ?? 0;
      const w = result.data?.warnings ?? [];
      const chunksFailed = result.data?.chunksFailed ?? 0;
      const chunksTotal = result.data?.chunksTotal ?? 0;
      setFormsExtracted(extracted);
      setWarnings(w);
      setExtractedTitle(result.data?.studyTitle ?? '');
      if (extracted === 0) {
        // Don't silently land the user on an empty CRF Builder — the more
        // informative thing is to show the warnings and let them retry or
        // delete the orphan project.
        setStage('error');
        // Headline summarizes the failure mode (all-chunks-failed vs
        // empty-document) — the per-chunk warnings render below in the
        // shared warnings list, so the user sees the full picture
        // instead of just the first warning.
        const headline =
          chunksFailed > 0 && chunksTotal > 0 && chunksFailed === chunksTotal
            ? `All ${chunksTotal} chunk${chunksTotal === 1 ? '' : 's'} could not be read — the reading service timed out or returned no usable result. Try again.`
            : chunksFailed > 0
              ? `${chunksFailed} of ${chunksTotal} chunks failed and the rest produced no forms.`
              : "The extractor couldn't find any CRF forms in this packet. Try a different document, or add forms manually from the Builder.";
        setError(headline);
        return;
      }
      // Partial extraction: at least one Pass-1 chunk failed entirely,
      // so some forms in the packet were skipped. Surface as a "partial"
      // stage (amber, not red) — the run isn't a failure, it's a
      // mostly-success that the user can either accept and move on with
      // (most packets won't be perfectly extractable in one shot when
      // Gemini is having capacity issues) or retry from scratch.
      if (chunksFailed > 0 && chunksTotal > 0) {
        setStage('partial');
        setError(
          `${chunksFailed} of ${chunksTotal} chunks couldn't be extracted (timeout or temporary service problem). ${extracted} form${extracted === 1 ? '' : 's'} from the other ${chunksTotal - chunksFailed} chunks ${extracted === 1 ? 'is' : 'are'} ready. You can keep these and add the missing forms manually, or retry the whole packet.`,
        );
        return;
      }
      setStage('done');
      setStatus(`Extracted ${extracted} form${extracted === 1 ? '' : 's'}.`);
      onComplete(projectId, extracted);
    } catch (err: any) {
      console.error('[UploadCrfPacketModal] failed:', err);
      // FunctionsError has .code like "functions/internal" and sometimes a
      // blank .message — surface both so the user has something useful.
      const code: string | undefined = err?.code;
      const rawMessage: string | undefined = err?.message;
      const display =
        rawMessage && rawMessage !== code && rawMessage !== 'internal'
          ? rawMessage
          : code
            ? `${code}${rawMessage ? ` — ${rawMessage}` : ''}`
            : 'Upload failed';
      setStage('error');
      setError(display);
    }
  };

  const busy = stage === 'creating' || stage === 'uploading' || stage === 'extracting';

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {existingProjectId ? 'Upload CRF' : 'Upload CRF Packet'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {existingProjectId
                ? 'Add forms to this project from a CRF document.'
                : 'Start a new project from an existing CRF packet — the study title will be pulled from the document.'}
            </p>
          </div>
          {!busy && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-4">
          {stage === 'picking' && (
            <>
              <NoPhiNotice />
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
                  dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400'
                }`}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                {file ? (
                  <div className="flex items-center justify-center gap-2 text-gray-700">
                    <FileText className="h-5 w-5 text-primary-600" />
                    <span className="text-sm">{file.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-700">Drag &amp; drop your CRF packet, or click to select</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, DOC, or DOCX · up to 50MB</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileSelected(f);
                  }}
                />
              </div>

              {!existingProjectId && (
                <div className="mt-3">
                  <label htmlFor="crf-packet-short-name" className="block text-xs font-medium text-gray-600 mb-1">
                    Study Short Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="crf-packet-short-name"
                    type="text"
                    value={shortName}
                    onChange={(e) => setShortName(e.target.value)}
                    maxLength={40}
                    placeholder="e.g. ONC-2024-001"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Identifies this study on every document the platform produces, including
                    the filename of each one. Letters, numbers, hyphens and underscores.
                  </p>
                </div>
              )}

              <label className="mt-3 flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={singleForm}
                  onChange={(e) => setSingleForm(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  This file is a <strong>single CRF</strong> — keep it as one form.
                  <span className="block text-xs text-gray-400">
                    Turn this off if the file is a packet containing several different forms.
                  </span>
                </span>
              </label>

              {singleForm && (
                <div className="mt-1">
                  <label htmlFor="crf-form-name" className="block text-xs font-medium text-gray-600 mb-1">
                    Form name
                  </label>
                  <input
                    id="crf-form-name"
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g. C-SSRS Follow-Up"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    The extracted form is saved under this name (defaults to the file name).
                  </p>
                </div>
              )}

            </>
          )}

          {busy && (
            <div className="flex flex-col items-center py-6 text-center">
              <Loader2 className="h-10 w-10 text-primary-600 animate-spin mb-3" />
              <p className="text-sm font-medium text-gray-800">{status}</p>
              <p className="text-xs text-gray-500 mt-1">
                {stage === 'extracting'
                  ? 'This can take 1–3 minutes for a large packet.'
                  : 'Please keep this window open.'}
              </p>
            </div>
          )}

          {stage === 'done' && (
            <div className="flex flex-col items-center py-4 text-center">
              <CheckCircle2 className="h-10 w-10 text-green-600 mb-3" />
              <p className="text-base font-semibold text-gray-900">
                Extracted {formsExtracted} form{formsExtracted === 1 ? '' : 's'}.
              </p>
              {extractedTitle && (
                <p className="text-sm text-gray-600 mt-2">
                  Study title: <span className="font-medium text-gray-900">{extractedTitle}</span>
                </p>
              )}
              {!extractedTitle && !existingProjectId && (
                <p className="text-xs text-amber-700 mt-2">
                  Couldn't detect a study title in the packet — you can rename the project from the sidebar.
                </p>
              )}
              {warnings.length > 0 && (
                <div className="mt-4 w-full text-left p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Notes</p>
                  <ul className="text-xs text-amber-700 list-disc pl-4 space-y-0.5">
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {stage === 'partial' && (
            <div className="flex flex-col items-center py-4 text-center">
              <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
              <p className="text-base font-semibold text-gray-900">
                Partial extraction — {formsExtracted} form{formsExtracted === 1 ? '' : 's'} ready
              </p>
              <p className="text-xs text-amber-700 mt-2 max-w-md">{error}</p>
              {extractedTitle && (
                <p className="text-sm text-gray-600 mt-3">
                  Study title: <span className="font-medium text-gray-900">{extractedTitle}</span>
                </p>
              )}
            </div>
          )}

          {stage === 'error' && (
            <div className="flex flex-col items-center py-4 text-center">
              <AlertTriangle className="h-10 w-10 text-red-500 mb-3" />
              <p className="text-sm font-medium text-gray-800">Extraction failed</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
              {warnings.length > 0 && (
                <div className="mt-4 w-full text-left p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Per-chunk warnings</p>
                  <ul className="text-xs text-amber-700 list-disc pl-4 space-y-0.5">
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          {stage === 'picking' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={run}
                disabled={!file || (!existingProjectId && !shortName.trim())}
                className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
              >
                {existingProjectId ? 'Extract Forms' : 'Create Project & Extract'}
              </button>
            </>
          )}

          {stage === 'done' && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
            >
              Done
            </button>
          )}

          {stage === 'partial' && (
            <>
              <button
                onClick={() => {
                  setStage('picking');
                  setError('');
                  setFile(null);
                }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Re-run extraction on the same packet (creates duplicates of the forms already extracted — clean those up afterwards)"
              >
                Try Again
              </button>
              <button
                onClick={() => {
                  // Accept the partial result and proceed into the
                  // workspace. The user already has the extracted forms
                  // saved server-side; this just closes the modal and
                  // lands them in the CRF Builder with what they got.
                  // They can add missing forms manually from there.
                  if (resolvedProjectId) {
                    onComplete(resolvedProjectId, formsExtracted);
                  }
                }}
                disabled={!resolvedProjectId}
                className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
              >
                Keep {formsExtracted} form{formsExtracted === 1 ? '' : 's'}
              </button>
            </>
          )}

          {stage === 'error' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setStage('picking');
                  setError('');
                }}
                className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
